import { useState, useRef } from 'react'

interface UsePaginationOptions<T> {
  perPage: number
  fetchPage: (currentPage: number, perPage: number) => Promise<Array<T>>
  startPage?: number
}
interface UsePaginationReturn<T> {
  data: Array<T>
  currentPage: number
  fetchNext: () => Promise<void>
  refetch: () => Promise<void>
  reset: () => void
  allFetched: boolean
  isFetching: boolean
}

interface PaginationState<T> {
  currentPage: number
  data: Array<T>
  isFetching: boolean
  allFetched: boolean
}

const usePagination = <T extends unknown>({
  perPage,
  fetchPage,
  startPage = 0,
}: UsePaginationOptions<T>): UsePaginationReturn<T> => {
  const initial: PaginationState<T> = {
    currentPage: startPage,
    data: [],
    isFetching: false,
    allFetched: false,
  }
  const [{ allFetched, currentPage, data, isFetching }, _setState] =
    useState<PaginationState<T>>(initial)

  // State is mirrored in a ref and written through synchronously, so guards and the page
  // number never read a stale render closure — `reset(); fetchNext()` in one tick works.
  const stateRef = useRef<PaginationState<T>>(initial)
  const commit = (patch: Partial<PaginationState<T>>): void => {
    stateRef.current = { ...stateRef.current, ...patch }
    _setState(stateRef.current)
  }

  // bumped by reset/refetch so a response from a superseded query is discarded
  const generationRef = useRef(0)

  const fetchNext = async () => {
    const { isFetching: fetching, allFetched: done, currentPage: page } = stateRef.current
    if (fetching || done) return

    const generation = generationRef.current
    commit({ isFetching: true })
    try {
      const newData = await fetchPage(page, perPage)
      if (generation !== generationRef.current) return

      commit({
        currentPage: stateRef.current.currentPage + 1,
        isFetching: false,
        allFetched: newData.length !== perPage,
        data: stateRef.current.data.concat(newData),
      })
    } catch (err) {
      if (generation === generationRef.current) commit({ isFetching: false })
      throw err
    }
  }

  const refetch = async () => {
    generationRef.current += 1
    const generation = generationRef.current
    commit({ currentPage: startPage, isFetching: true, allFetched: false })
    try {
      const newData = await fetchPage(startPage, perPage)
      if (generation !== generationRef.current) return

      commit({
        currentPage: startPage + 1,
        isFetching: false,
        allFetched: newData.length !== perPage,
        data: newData,
      })
    } catch (err) {
      if (generation === generationRef.current) commit({ isFetching: false })
      throw err
    }
  }

  const reset = () => {
    generationRef.current += 1
    commit({ currentPage: startPage, data: [], isFetching: false, allFetched: false })
  }

  return { currentPage, fetchNext, data, allFetched, refetch, isFetching, reset }
}

export default usePagination
